import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Edit3, FolderOpen, Plus, Trash2 } from "lucide-react";

import { salesCategoriesGetAll } from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";

export default function SalesCategoriesList() {
  const navigate = useNavigate();

  const { data: salesCategories = [] } = useQuery(
    convexQuery(salesCategoriesGetAll, {})
  );

  // V4.1: Route-based navigation for delete modal
  const navigateToDelete = (id: string, name: string) => {
    void navigate({
      to: "/settings/sales-categories/$id/delete",
      params: { id },
      search: { name },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="h2 font-heading text-foreground text-balance">
            Sales Categories
          </h1>
          <p className="text-muted-foreground text-pretty">
            Manage your sales rates for items in your menus.
          </p>
        </div>
        {/* V4.1: Route-based create for URL context tracking */}
        <Button
          onClick={() => navigate({ to: "/settings/sales-categories/new" })}
          size="lg"
        >
          <Plus className="size-4" />
          Add Sales Category
        </Button>
      </div>

      <div
        className="border-muted-foreground/5 bg-background w-full max-w-4xl overflow-hidden rounded-md border shadow-xs"
        id="salesCategoriesList"
      >
        {/* header */}
        <div className="border-muted-foreground/5 bg-muted/30 h-11 border-b px-6">
          <p className="label text-muted-foreground">Name</p>
        </div>
        {salesCategories.length === 0 && (
          <Empty className="py-12">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FolderOpen />
              </EmptyMedia>
              <EmptyTitle>No sales categories yet</EmptyTitle>
              <EmptyDescription>
                Create sales categories to organize reporting for your menu
                items.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
        {[...salesCategories]?.map((item, i) => (
          <div
            className="border-muted-foreground/5 hover:bg-sidebar/50 flex h-11 items-center justify-between border-b px-6 transition-[color,box-shadow] last:border-b-0"
            id={`salesCategory${i}`}
            key={item._id}
          >
            <p className="text-foreground">{item.name}</p>
            <div className="flex items-center justify-end space-x-2">
              <button
                aria-label={`Edit ${item.name}`}
                className="bg-muted h-9 w-9 rounded-full p-2"
                onClick={() =>
                  navigate({
                    to: `/settings/sales-categories/${item._id}/edit`,
                  })
                }
                type="button"
              >
                <Edit3 className="text-foreground size-5" />
              </button>
              <button
                aria-label={`Delete ${item.name}`}
                className="bg-destructive/10 h-9 w-9 rounded-full p-2"
                onClick={() => navigateToDelete(item._id, item.name)}
                type="button"
              >
                <Trash2 className="text-destructive size-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
